/**
 * Grafana Deep-Link Utilities
 *
 * Functions for generating Grafana/Loki explore URLs with proper query encoding.
 * Used by LogViewer and deployment detail views.
 */

// ===============================================================
// CONFIGURATION
// ===============================================================

/**
 * Get the configured Grafana base URL.
 * Checks window.BRUTAL_GRAFANA_URL first, then falls back to default.
 */
export function getGrafanaBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const grafanaUrl = window.BRUTAL_GRAFANA_URL ?? null;
    if (grafanaUrl !== null && grafanaUrl !== '') {
      return grafanaUrl;
    }
  }
  // Default Grafana URL - Hub server
  return 'http://10.66.66.3:3000';
}

// ===============================================================
// TYPES
// ===============================================================

/** Filter parameters for building Loki queries */
export interface LokiQueryFilters {
  environment: string | null;
  artifact: string | null;
  deploymentId: string | null;
  level: string | null;
  host: string | null;
}

/** Time range for Grafana explore */
export interface GrafanaTimeRange {
  from: string;
  to: string;
}

// ===============================================================
// QUERY BUILDING
// ===============================================================

/**
 * Build a LogQL label selector from filter parameters.
 *
 * @example
 * buildLokiLabelSelector({ environment: 'production', artifact: 'api' })
 * // => '{job="brutal-agent", environment="production", artifact="api"}'
 */
export function buildLokiLabelSelector(filters: Partial<LokiQueryFilters>): string {
  const matchers: string[] = ['job="brutal-agent"'];

  const env = filters.environment ?? null;
  if (env !== null && env !== '') {
    matchers.push(`environment="${escapeLogQLValue(env)}"`);
  }

  const artifact = filters.artifact ?? null;
  if (artifact !== null && artifact !== '') {
    matchers.push(`artifact="${escapeLogQLValue(artifact)}"`);
  }

  const deploymentId = filters.deploymentId ?? null;
  if (deploymentId !== null && deploymentId !== '') {
    matchers.push(`deployment_id="${escapeLogQLValue(deploymentId)}"`);
  }

  const level = filters.level ?? null;
  if (level !== null && level !== '') {
    matchers.push(`level="${escapeLogQLValue(level)}"`);
  }

  const host = filters.host ?? null;
  if (host !== null && host !== '') {
    matchers.push(`host="${escapeLogQLValue(host)}"`);
  }

  return `{${matchers.join(', ')}}`;
}

/**
 * Build a full LogQL query with optional line filters.
 *
 * @example
 * buildLokiQuery({ environment: 'production' }, 'error')
 * // => '{job="brutal-agent", environment="production"} |= "error"'
 */
export function buildLokiQuery(
  filters: Partial<LokiQueryFilters>,
  lineFilter: string | null = null,
): string {
  const selector = buildLokiLabelSelector(filters);

  if (lineFilter !== null && lineFilter !== '') {
    return `${selector} |= "${escapeLogQLValue(lineFilter)}"`;
  }

  return selector;
}

/**
 * Escape special characters in LogQL string values.
 * Escapes backslashes, double quotes, and newlines.
 */
function escapeLogQLValue(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

// ===============================================================
// URL BUILDING
// ===============================================================

/**
 * Generate a Grafana Explore URL for a Loki query.
 *
 * @param query - LogQL query string
 * @param timeRange - Optional time range (defaults to last hour)
 * @param baseUrl - Optional Grafana base URL (defaults to configured URL)
 *
 * @example
 * buildGrafanaExploreUrl('{job="brutal-agent"}')
 * // => 'http://10.66.66.3:3000/explore?orgId=1&left=...'
 */
export function buildGrafanaExploreUrl(
  query: string,
  timeRange: GrafanaTimeRange | null = null,
  baseUrl: string | null = null,
): string {
  const grafanaUrl = baseUrl ?? getGrafanaBaseUrl();
  const range = timeRange ?? { from: 'now-1h', to: 'now' };

  // Encode for URL (Grafana uses a special format for the left pane)
  // Format: /explore?orgId=1&left=["now-1h","now","Loki",{"expr":"..."}]
  const leftParam = JSON.stringify([range.from, range.to, 'Loki', { expr: query }]);

  return `${grafanaUrl}/explore?orgId=1&left=${encodeURIComponent(leftParam)}`;
}

/**
 * Generate a Grafana Explore URL from filter parameters.
 * Convenience function that combines buildLokiQuery and buildGrafanaExploreUrl.
 *
 * @example
 * buildGrafanaUrlFromFilters({ environment: 'production', artifact: 'api' })
 */
export function buildGrafanaUrlFromFilters(
  filters: Partial<LokiQueryFilters>,
  options: {
    lineFilter?: string | null;
    timeRange?: GrafanaTimeRange | null;
    baseUrl?: string | null;
  } = {},
): string {
  const query = buildLokiQuery(filters, options.lineFilter ?? null);
  return buildGrafanaExploreUrl(query, options.timeRange ?? null, options.baseUrl ?? null);
}

/**
 * Generate a Grafana URL for viewing logs from a specific deployment.
 *
 * @param deploymentId - The deployment ID to filter logs by
 * @param options - Additional options for time range and base URL
 */
export function buildDeploymentLogsUrl(
  deploymentId: string,
  options: {
    environment?: string | null;
    artifact?: string | null;
    timeRange?: GrafanaTimeRange | null;
    baseUrl?: string | null;
  } = {},
): string {
  return buildGrafanaUrlFromFilters(
    {
      deploymentId,
      environment: options.environment ?? null,
      artifact: options.artifact ?? null,
      level: null,
      host: null,
    },
    {
      timeRange: options.timeRange ?? null,
      baseUrl: options.baseUrl ?? null,
    },
  );
}
